import React from 'react'

import {api} from '../services/api'
import {FACE_OFF_NOT_FOUND} from '../services/api/faceoff'
import {APIResponse} from '../services/api/types'
import {FaceOffRunDetail} from '../services/coreApi/faceoff'
import {FetchStatus} from './useRecognitionAttempts'

export interface FaceOffLoadState<T> {
  status: FetchStatus
  data: T | null
  notFound: boolean
}

/**
 * Runs one Face Off api call when `key` changes and tracks its state. A 404
 * resolves to `notFound`, any other failure to `rejected`.
 */
export const useFaceOffLoad = <T>(
  key: string,
  load: () => Promise<APIResponse<T>>,
): FaceOffLoadState<T> => {
  const [state, setState] = React.useState<FaceOffLoadState<T>>({
    status: 'pending',
    data: null,
    notFound: false,
  })

  React.useEffect(() => {
    let cancelled = false
    setState({status: 'pending', data: null, notFound: false})

    load().then((response) => {
      if (cancelled) return
      if (response.result === 'success') {
        setState({status: 'resolved', data: response.data, notFound: false})
      } else {
        setState({
          status: 'rejected',
          data: null,
          notFound: response.error.message === FACE_OFF_NOT_FOUND,
        })
      }
    })

    return (): void => {
      cancelled = true
    }
    // `key` names everything `load` reads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  return state
}

/** Loads one run's diff. */
export const useFaceOffRun = (
  runId: string,
): FaceOffLoadState<FaceOffRunDetail> =>
  useFaceOffLoad(runId, () => api.faceoff.getFaceOffRun(runId))
